import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Button, Menu, MenuItem, Paper, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useKandidaten } from '../../api/kandidaten.ts';
import { zonderLeeg } from '../../api/mutaties.ts';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { Lijstpaginering } from '../../componenten/Paginering.tsx';
import { Zoekveld } from '../../componenten/Zoekveld.tsx';
import { useBewaardeWaarde } from '../../hooks/useBewaardeWaarde.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { FilterChip, FilterPopover } from './EigenschapFilter.tsx';
import { KandidaatFormulier } from './KandidaatFormulier.tsx';
import { KandidaatUitCvDialoog } from './KandidaatUitCvDialoog.tsx';
import { KandidatenTabel } from './KandidatenTabel.tsx';
import {
  type Eigenschap,
  geldigeKolommen,
  geldigeSortering,
  heeftSortering,
  kandidaatEigenschappen,
  leesFilter,
  leesSortering,
  naarApiFilter,
  schrijfFilter,
  sorteerparameters,
  volgendeSortering,
  wisAlleFilters,
} from './kandidaatEigenschappen.ts';
import { KolomKeuze } from './KolomKeuze.tsx';

const filterbaar = kandidaatEigenschappen.filter((eigenschap) => eigenschap.filter);

/**
 * Het overzicht van kandidaten. De gebruiker kiest zelf de kolommen, sorteert op elke kolom en filtert op elke
 * eigenschap. Filters en sortering staan in het adres, zodat een lijst te delen is; de kolommen en de laatst
 * gekozen sortering blijven per browser bewaard.
 * @returns De pagina.
 */
export function KandidatenPagina() {
  const { zoek, offset, limiet, krijg: uitAdres, zet, zetOffset } = useZoekparameters();
  const [kolommen, zetKolommen] = useBewaardeWaarde('kandidaten.kolommen', geldigeKolommen);
  const [bewaardeSortering, bewaarSortering] = useBewaardeWaarde('kandidaten.sortering', geldigeSortering);
  // Zonder sortering in het adres geldt de laatst gekozen sortering; een link met sortering gaat voor.
  const metAdresSortering = heeftSortering(uitAdres);
  const krijg = (naam: string) =>
    !metAdresSortering && (naam === 'sorteer' || naam === 'richting')
      ? (sorteerparameters(bewaardeSortering)[naam] ?? '')
      : uitAdres(naam);
  const kandidaten = useKandidaten({ ...naarApiFilter(krijg), ...zonderLeeg({ zoek, limiet, offset }) });
  const adresVeld = metAdresSortering ? leesSortering(uitAdres).veld : null;
  const adresRichting = metAdresSortering ? leesSortering(uitAdres).richting : null;
  useEffect(() => {
    // Een sortering die via het adres binnenkomt, bijvoorbeeld uit een gedeelde link, wordt ook onthouden.
    if (
      adresVeld &&
      adresRichting &&
      (adresVeld !== bewaardeSortering.veld || adresRichting !== bewaardeSortering.richting)
    ) {
      bewaarSortering({ veld: adresVeld, richting: adresRichting });
    }
  }, [adresVeld, adresRichting, bewaardeSortering, bewaarSortering]);
  const [bewerkt, setBewerkt] = useState<{ eigenschap: Eigenschap; anker: HTMLElement } | null>(null);
  const [filtermenu, setFiltermenu] = useState<HTMLElement | null>(null);
  const [nieuw, setNieuw] = useState(false);
  const [uitCv, setUitCv] = useState(false);
  const navigeer = useNavigate();

  const sortering = leesSortering(krijg);
  const actieveFilters = kandidaatEigenschappen.flatMap((eigenschap) => {
    const stand = leesFilter(eigenschap, krijg);
    return stand ? [{ eigenschap, stand }] : [];
  });
  const gefilterd = new Set(actieveFilters.map(({ eigenschap }) => eigenschap.sleutel));
  const zichtbaar = kandidaatEigenschappen.filter((eigenschap) => kolommen.includes(eigenschap.sleutel));

  return (
    <Pagina
      titel="Kandidaten"
      acties={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setUitCv(true)}>
            Uit cv
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNieuw(true)}>
            Nieuwe kandidaat
          </Button>
        </Stack>
      }
    >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
          <Zoekveld
            waarde={zoek}
            onChange={(waarde) => zet({ zoek: waarde })}
            placeholder="Zoek op naam, e-mail of telefoon"
          />
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<FilterListIcon />}
              onClick={(gebeurtenis) => setFiltermenu(gebeurtenis.currentTarget)}
              aria-haspopup="menu"
            >
              Filter toevoegen
            </Button>
            <KolomKeuze zichtbaar={kolommen} onChange={zetKolommen} />
          </Stack>
        </Stack>
        {actieveFilters.length > 0 && (
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, mt: 2, alignItems: 'center' }}>
            {actieveFilters.map(({ eigenschap, stand }) => (
              <FilterChip
                key={eigenschap.sleutel}
                eigenschap={eigenschap}
                stand={stand}
                onKlik={(anker) => setBewerkt({ eigenschap, anker })}
                onWis={() => zet(schrijfFilter(eigenschap, null, krijg))}
              />
            ))}
            <Button size="small" onClick={() => zet(wisAlleFilters())}>
              Filters wissen
            </Button>
          </Stack>
        )}
      </Paper>
      <Menu anchorEl={filtermenu} open={Boolean(filtermenu)} onClose={() => setFiltermenu(null)}>
        {filterbaar.map((eigenschap) => (
          <MenuItem
            key={eigenschap.sleutel}
            dense
            selected={gefilterd.has(eigenschap.sleutel)}
            onClick={() => {
              if (filtermenu) {
                setBewerkt({ eigenschap, anker: filtermenu });
              }
              setFiltermenu(null);
            }}
          >
            {eigenschap.label}
          </MenuItem>
        ))}
      </Menu>
      {bewerkt && (
        <FilterPopover
          key={bewerkt.eigenschap.sleutel}
          eigenschap={bewerkt.eigenschap}
          stand={leesFilter(bewerkt.eigenschap, krijg)}
          anker={bewerkt.anker}
          onToepassen={(stand) => zet(schrijfFilter(bewerkt.eigenschap, stand, krijg))}
          onSluit={() => setBewerkt(null)}
        />
      )}
      <Paper sx={{ p: 2 }}>
        <Laadstatus query={kandidaten}>
          {(lijst) => (
            <KandidatenTabel
              kandidaten={lijst}
              kolommen={zichtbaar}
              sortering={sortering}
              gefilterd={gefilterd}
              onSorteer={(veld) => {
                const volgende = volgendeSortering(sortering, veld);
                bewaarSortering(leesSortering((naam) => volgende[naam] ?? ''));
                zet(volgende);
              }}
              onFilter={(eigenschap, anker) => setBewerkt({ eigenschap, anker })}
            />
          )}
        </Laadstatus>
        <Lijstpaginering offset={offset} limiet={limiet} aantal={kandidaten.data?.length ?? 0} onOffset={zetOffset} />
      </Paper>
      <KandidaatUitCvDialoog
        open={uitCv}
        onSluit={() => setUitCv(false)}
        onAangemaakt={(kandidaat) => navigeer(`/kandidaten/${kandidaat.id}`)}
      />
      <KandidaatFormulier
        open={nieuw}
        onSluit={() => setNieuw(false)}
        onOpgeslagen={(kandidaat) => void navigeer(`/kandidaten/${kandidaat.id}`)}
      />
    </Pagina>
  );
}
